import React, { ReactNode } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { SimplePermissionWrapper } from './SimplePermissionWrapper';
import { fieldMappingService } from '../config/fieldMapping';
import { FormFieldGroup } from './forms/FormFieldGroup';

interface PermissionAwareFieldProps {
  /**
   * The form field identifier (matches your formData field names)
   * This will be mapped to the JSON permission field automatically
   */
  fieldId: string;

  /**
   * Optional override for the form section (will try to auto-detect from mapping)
   */
  formSection?: string;

  /**
   * The current request stage for stage-based permissions
   */
  requestStage?: string;

  /**
   * The request ID (needed for partner contributor access level lookups)
   */
  requestId?: string;

  /**
   * The form component to wrap with permissions
   */
  children: ReactNode;

  /**
   * Show debug information about permission mapping
   */
  showDebugInfo?: boolean;

  /**
   * Component to show when loading permissions
   */
  loadingComponent?: ReactNode;

  /**
   * Component to show when field is hidden
   */
  hiddenComponent?: ReactNode;

  /**
   * Component to show when field is read-only
   */
  readOnlyComponent?: ReactNode;

  /**
   * Whether to fallback to read-only instead of hiding when permissions deny write
   */
  fallbackToReadOnly?: boolean;

  /**
   * Show full details (advanced view) or simplified view (basic view)
   * When false, hides fields in basic view mode
   */
  showFullDetails?: boolean;

  /**
   * Style to apply to the wrapper container View.
   * Use when PermissionAwareField is a direct child of FormRow
   * (e.g. wrapping a FormColumn) and needs flex layout participation.
   */
  containerStyle?: StyleProp<ViewStyle>;
}

/**
 * Enhanced field wrapper that automatically maps form fields to JSON permissions
 *
 * Usage:
 * <PermissionAwareField fieldId="requestorEmail">
 *   <FormInput
 *     label="Requestor Email"
 *     value={formData.requestorEmail}
 *     onChangeText={(value) => updateFormData('requestorEmail', value)}
 *   />
 * </PermissionAwareField>
 */
export const PermissionAwareField: React.FC<PermissionAwareFieldProps> = ({
  fieldId,
  formSection,
  requestStage,
  requestId,
  children,
  showDebugInfo = false,
  loadingComponent,
  hiddenComponent,
  readOnlyComponent,
  fallbackToReadOnly = true,
  showFullDetails = true,
  containerStyle,
}) => {
  // Try to find mapping for this field
  const findMapping = () => {
    // Prefer explicit form section when provided (e.g. global `uom` maps in multiple JSON sections).
    if (formSection) {
      const direct = fieldMappingService.getJsonFieldMapping(formSection, fieldId);
      if (direct) {
        return direct;
      }
    }

    for (const section of fieldMappingService.getFormSections()) {
      const mapping = fieldMappingService.getJsonFieldMapping(section, fieldId);
      if (mapping) {
        return mapping;
      }
    }

    return null;
  };

  const mapping = findMapping();

  // If no mapping found, render without permission wrapper but show debug info if requested
  if (!mapping) {
    if (showDebugInfo) {
      console.warn(`No permission mapping found for field: ${fieldId} in section: ${formSection || 'auto-detect'}`);
    }

    // Return children without permission wrapper (respect showFullDetails even without mapping)
    return showFullDetails ? <FormFieldGroup>{children}</FormFieldGroup> : null;
  }

  if (showDebugInfo) {
    console.log(`Permission mapping found: ${mapping.formField} -> ${mapping.jsonSection}.${mapping.jsonField}`);
  }

  return (
    <SimplePermissionWrapper
      fieldType="standard-field"
      fieldName={mapping.jsonField}
      section={mapping.jsonSection}
      requestStage={requestStage}
      requestId={requestId}
      showDebugInfo={showDebugInfo}
      loadingComponent={loadingComponent}
      hiddenComponent={hiddenComponent}
      readOnlyComponent={readOnlyComponent}
      fallbackToReadOnly={fallbackToReadOnly}
      showFullDetails={showFullDetails}
      containerStyle={containerStyle}
    >
      {children}
    </SimplePermissionWrapper>
  );
};

/**
 * Higher-order component version for easier integration
 */
export const withFieldPermissions = <P extends object>(
  Component: React.ComponentType<P>,
  fieldId: string,
  formSection?: string,
  options?: {
    showDebugInfo?: boolean;
    fallbackToReadOnly?: boolean;
    loadingComponent?: ReactNode;
    hiddenComponent?: ReactNode;
    readOnlyComponent?: ReactNode;
  }
) => {
  return React.forwardRef<any, P>((props, ref) => (
    <PermissionAwareField
      fieldId={fieldId}
      formSection={formSection}
      showDebugInfo={options?.showDebugInfo}
      fallbackToReadOnly={options?.fallbackToReadOnly}
      loadingComponent={options?.loadingComponent}
      hiddenComponent={options?.hiddenComponent}
      readOnlyComponent={options?.readOnlyComponent}
    >
      <Component {...props} ref={ref} />
    </PermissionAwareField>
  ));
};

/**
 * Hook for getting permission info for a field
 */
export const useFieldPermissionMapping = (fieldId: string, formSection?: string) => {
  const findMapping = () => {
    for (const section of fieldMappingService.getFormSections()) {
      const mapping = fieldMappingService.getJsonFieldMapping(section, fieldId);
      if (mapping) {
        return mapping;
      }
    }

    if (formSection) {
      return fieldMappingService.getJsonFieldMapping(formSection, fieldId);
    }

    return null;
  };

  const mapping = findMapping();

  return {
    mapping,
    hasMapping: mapping !== null,
    jsonSection: mapping?.jsonSection,
    jsonField: mapping?.jsonField,
    formSection: mapping?.formSection,
    formField: mapping?.formField,
    component: mapping?.component,
    description: mapping?.description,
  };
};